import React, { createContext, useState, useEffect, useMemo, useCallback } from "react";
import apiClient from "../auth/apiClient";
import NotificationFeedbackService from "../services/NotificationFeedbackService";

export const NotificationFeedbackContext = createContext();

const notificationFeedbackService = new NotificationFeedbackService(apiClient);

/**
 * Maps the API response (releasenotessettings DTO) to the internal settings shape
 * used by the components and visibility utilities.
 */
const mapApiResponse = (data) => {
  if (!data) return null;
  return {
    releaseNotesEnabled: data.releaseNotesEnabled,
    displayPeriodStart: data.displayFrom,
    displayPeriodEnd: data.displayTo,
    releaseNotesUrl: data.releaseNotesUrl,
    feedbackEnabled: data.feedbackEnabled,
    feedbackUrl: data.feedbackLink,
    feedbackLabel: data.feedbackButtonLabel,
  };
};

const NotificationFeedbackProvider = ({ children }) => {
  const [state, setState] = useState({
    settings: null,
    loading: true,
    error: false,
  });

  const fetchSettings = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const response = await notificationFeedbackService.getSettings();
      setState({ settings: mapApiResponse(response.data), loading: false, error: false });
    } catch (err) {
      setState({ settings: null, loading: false, error: true });
    }
  }, []);

  const applyExternalSettings = useCallback((newSettings) => {
    if (newSettings == null) return;
    setState({ settings: newSettings, loading: false, error: false });
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const value = useMemo(
    () => ({
      ...state,
      fetchSettings,
      applyExternalSettings,
    }),
    [state, fetchSettings, applyExternalSettings]
  );

  return (
    <NotificationFeedbackContext.Provider value={value}>
      {children}
    </NotificationFeedbackContext.Provider>
  );
};

export default NotificationFeedbackProvider;
